<template>
  <div
    class="action"
    :class="{ oneLine, completed }"
    @click="actionClicked"
    @click.stop="showCommentInput"
  >
    <SQDCEventActionModalComponent
      v-if="showEventModal && siteId && actionType"
      :source="{
        kind: 'action-edit',
        actionId: action.id,
        eventId: event?.id,
        actionType,
        workflowInstanceId: workflowContext?.instance.workflowInstanceId,
        workflowStepId: workflowContext?.step.workflowStepId,
      }"
      :site-id="siteId"
      :board-id="boardId"
      @close="showEventModal = false"
    />
    <EventActionRowLayout
      class="h-100 w-100"
      :padded-content="!oneLine"
      :side-class="recentlyChanged"
    >
      <template #id>
        <div class="id lisaui:flex lisaui:items-center header">
          <IDBadge
            :id="action.oldId ?? action.id"
            style="min-width: calc(8ch + 10.5px)"
            class="lisaui:text-center pointerAll"
            :color="actionTypeConfig?.idColor"
            :prefix="action.oldId ? '' : getActionPrefix(actionTypes, action.typeId)"
            @click="idBadgeClicked"
          />
        </div>
      </template>
      <template #header>
        <div class="lisaui:flex lisaui:items-center header">
          <div class="assignee">
            <UserBadge
              :user="action.Assignee"
              class="bounceTag"
              @clic.stop="setUserFilter(action.Assignee, $event)"
            />
          </div>

          <div v-if="actionButtonModuleBadges.length > 0" class="button-module-badges">
            <span
              v-for="badge in actionButtonModuleBadges"
              :key="badge.key"
              class="button-module-badge"
              :class="`button-module-badge--${badge.severity}`"
            >
              <span class="button-module-badge__icon">
                <i :class="badge.icon" />
              </span>
              <span class="button-module-badge__label">
                {{ badge.label }}
              </span>
            </span>
          </div>

          <div v-if="$slots.leadingHeaderAdditions" class="pr-1">
            <slot name="leadingHeaderAdditions" />
          </div>

          <div v-if="oneLine && oneLineContent" class="oneLineContent">
            {{ oneLineContent }}
          </div>
          <div v-else class="grow"></div>

          <div class="headerAdditions grow">
            <AttachmentBadge
              :files="[
                ...(action.Folder?.files.map((file) => ({ ...file, name: file.fileName })) ?? []),
              ]"
            />
          </div>

          <div class="deadline">
            <ActionDeadlineBadge :action="action" />
          </div>
        </div>
      </template>
      <template #left>
        <div
          class="linked lisaui:flex h-100 align-items-start lisaui:justify-between lisaui:px-2"
        />
      </template>
      <template #content>
        <div v-if="!oneLine" class="content">
          <div v-if="actionEventInfoTitle" class="action-title-row">
            <span class="action-title-row__text">{{ actionEventInfoTitle }}</span>
          </div>
          <div
            v-if="actionRowFieldsOnEAB.length > 0"
            class="action-row-fields"
            :class="{ single: actionRowFieldsOnEAB.length === 1 }"
          >
            <template v-if="actionRowFieldsOnEAB.length === 1">
              <div class="action-row-field-value single-value">
                <span
                  v-if="actionRowFieldsOnEAB[0].badge"
                  class="button-module-badge"
                  :class="`button-module-badge--${actionRowFieldsOnEAB[0].badge.severity}`"
                >
                  <span class="button-module-badge__icon">
                    <i :class="actionRowFieldsOnEAB[0].badge.icon" />
                  </span>
                  <span class="button-module-badge__label">
                    {{ actionRowFieldsOnEAB[0].badge.label }}
                  </span>
                </span>
                <SQDCIshikawaDiagram
                  v-else-if="actionRowFieldsOnEAB[0].ishikawaDiagram"
                  class="action-row-field-diagram"
                  :name="actionRowFieldsOnEAB[0].ishikawaDiagram.name"
                  :reasons="actionRowFieldsOnEAB[0].ishikawaDiagram.reasons"
                  :areas="actionRowFieldsOnEAB[0].ishikawaDiagram.areas"
                />
                <template v-else>
                  {{ actionRowFieldsOnEAB[0].value }}
                </template>
              </div>
            </template>
            <template v-else>
              <template v-for="field in actionRowFieldsOnEAB" :key="field.key">
                <div class="action-row-field-label text-truncate">
                  {{ field.label }}
                </div>
                <div
                  class="action-row-field-value"
                  :class="{ 'text-truncate': !field.ishikawaDiagram }"
                >
                  <span
                    v-if="field.badge"
                    class="button-module-badge"
                    :class="`button-module-badge--${field.badge.severity}`"
                  >
                    <span class="button-module-badge__icon">
                      <i :class="field.badge.icon" />
                    </span>
                    <span class="button-module-badge__label">
                      {{ field.badge.label }}
                    </span>
                  </span>
                  <SQDCIshikawaDiagram
                    v-else-if="field.ishikawaDiagram"
                    class="action-row-field-diagram"
                    :name="field.ishikawaDiagram.name"
                    :reasons="field.ishikawaDiagram.reasons"
                    :areas="field.ishikawaDiagram.areas"
                  />
                  <template v-else>
                    {{ field.value }}
                  </template>
                </div>
              </template>
            </template>
          </div>
          <SQDCWorkflowProceedButtons
            v-if="workflowContext != null && siteId != null && event != null"
            :workflow-instance-id="workflowContext.instance.workflowInstanceId"
            :workflow-step-id="workflowContext.step.workflowStepId"
            :event="event"
            :small="true"
            :site-id="siteId"
            :board-id="boardId"
            :disabled="readOnly"
          />
          <EventActionCommentSection
            v-model:comment-visible="commentVisible"
            class="hideOnMobile"
            :action-id="action.id"
            :revisions="action.SQDCActionRevisions"
            :event-action-type-fields="actionType?.SQDCEventActionTypeFields ?? []"
            :archived="readOnly"
            :original-board-id="event?.originalBoardId"
          />
          <GithubPullRequestInfo
            v-if="isIntern() && githubPullRequests.length > 0"
            :task-id="action.id"
            :pull-requests="githubPullRequests"
          />
        </div>
      </template>

      <template #controls>
        <div class="control-rail">
          <div class="status" :class="{ pointerNone: readOnly }">
            <ActionStatusCircle
              :action="action"
              :event="event"
              :disable-popup="readOnly"
              style="height: 1.5rem; aspect-ratio: 1/1"
            >
              <template v-if="userIdForAnonymStatusChange == null" #default="{ resetTimer }">
                <SearchSelect
                  v-model="userIdForAnonymStatusChange"
                  :mode="SelectMode.USER"
                  :state="false"
                  :placeholder="
                    t('components.shared.searchSelect.UserSelect.selectUser').toString()
                  "
                  :filters="userFilters"
                  @close="resetTimer(700)"
                />
                <div class="lisaui:py-1">
                  {{ t("components.event-action.action.ActionComponent.selectUser") }}
                </div>
              </template>
            </ActionStatusCircle>
          </div>
          <i
            v-if="!oneLine"
            class="fas fa-angle-up collapseButton"
            style="font-size: 1.5em"
            @click.stop="handleOneLine"
          />
        </div>
      </template>
    </EventActionRowLayout>
    <slot name="editComponent" />
  </div>
</template>

<script setup lang="ts">
import { type PropType, ref, computed, inject } from "vue";
import {
  SQDCActionStatus,
  SQDCEventActionTypeFieldType,
  SQDCEventTypeFieldRelation,
  useGetCostCentersForRainbowTileQuery,
  useHLPCategoriesQuery,
  useGetUserForRevisionListQuery,
  useQAErrorCataloguesQuery,
  useSQDCEventActionTypesQuery,
  useRevisionTechnicalPlacesQuery,
  type EventListEventFieldsFragment,
} from "@/graphql";
import { useTranslation } from "@/language/translation";
import UserBadge from "@/components/badges/UserBadge.vue";
import {
  RecentlyChangedAlternativeEnum,
  getRecentlyChangedAlternative,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import store from "@/store";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { useProvidedBoardContext } from "@/modules/sqdc/helper/EABConfigHelper";
import { hasRight } from "@/functions/authorizationState";
import {
  formatActionEventInfoText,
  getActionPrefix,
  getCardsForRevision,
  type EABActionTypeType,
  type EABEventActionType,
  type EABEventType,
} from "./EventActionHelper";
import IDBadge from "@/components/badges/IDBadge.vue";
import ActionDeadlineBadge from "./ActionDeadlineBadge.vue";
import ActionStatusCircle from "./ActionStatusCircle.vue";
import EventActionCommentSection from "./EventActionCommentSection.vue";
import AttachmentBadge from "@/components/badges/AttachmentBadge.vue";
import SQDCIshikawaDiagram from "./modals/SQDCIshikawaDiagram.vue";
import type { ReasonType } from "./modals/SQDCIshikawaDraft";
import { normalizeIshikawaConfig, type IshikawaAreaConfig } from "./modals/ishikawaConfig";
import CreatorFilter from "@/components/event-action/filter/filterImplementations/CreatorFilter.ts";
import { DateTime } from "luxon";
import { ActionFilterInjectionKey } from "@/components/event-action/filter/FilterTypes.ts";
import getDefaultEABFilter from "@/views/event-action/EventActionBoardDefaultFilter.ts";
import SQDCEventActionModalComponent from "./SQDCEventActionModalComponent.vue";
import SQDCWorkflowProceedButtons from "./SQDCWorkflowProceedButtons.vue";
import { isRecentlyChanged } from "@/components/event-action/helper/EventBoardRecentlyChangedHelper";
import { useSQDCWorkflowInstancesForItem } from "../../workflow/useSQDCWorkflows";
import { formatDate } from "@/modules/lisaChart/helper/ChartTileHelper.ts";
import { getSQDCWorkflowItemContext } from "../../workflow/domain";
import { hasConfiguredAccessRight } from "@/functions/accessRightHelper";
import {
  formatSQDCButtonModuleValue,
  getSQDCButtonModuleDisplayOption,
  isSQDCButtonModuleType,
  type SQDCButtonModuleOption,
} from "@/modules/sqdc/helper/SQDCButtonModuleHelper";
import { formatSQDCRepairWindowEnumValue } from "@/modules/sqdc/helper/SQDCRepairWindowHelper";
import EventActionRowLayout from "./EventActionRowLayout.vue";
import GithubPullRequestInfo from "@/modules/github/components/GithubPullRequestInfo.vue";
import type { GithubActionPullRequest } from "@/modules/github/GithubPullRequestTypes";
import { isIntern } from "@/functions/environment";

const props = defineProps({
  action: {
    type: Object as PropType<EABEventActionType>,
    required: true,
  },
  event: {
    type: Object as PropType<EABEventType | undefined>,
    required: false,
    default: undefined,
  },
  openEditExternally: {
    type: Boolean,
    default: false,
  },
  completed: {
    type: Boolean,
    default: false,
  },
  archived: {
    type: Boolean,
    default: false,
  },
  forceOneLine: {
    type: Boolean,
    default: false,
  },
  actionTypes: {
    type: Array as PropType<EABActionTypeType[]>,
    required: false,
    default: () => [],
  },
  boardId: {
    type: Number,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
  githubPullRequests: {
    type: Array as PropType<GithubActionPullRequest[]>,
    default: () => [],
  },
});
const { siteId: providedSiteId } = useProvidedBoardContext();
const siteId = computed(() => props.siteId ?? providedSiteId.value);
const readOnly = computed(() => props.archived || !hasRight("sqdc.write", siteId.value));
const { instances: workflowInstances } = useSQDCWorkflowInstancesForItem(
  computed(() => ({ actionId: props.action.id })),
);
const workflowContext = computed(() =>
  getSQDCWorkflowItemContext(workflowInstances.value, { actionId: props.action.id }),
);
const emit = defineEmits(["open-edit-component", "click", "oneLine"]);
const commentVisible = ref(false);
function showCommentInput() {
  if (!readOnly.value) commentVisible.value = true;
}
const { t, locale } = useTranslation();
const userIdForAnonymStatusChange = computed({
  get() {
    return store.getters["taskComment/getTaskCommentatorId"];
  },
  set(value: number | null) {
    store.dispatch("taskComment/setTaskCommentatorId", value);
  },
});
const oneLine = usePropsAsDefault({
  dynamicProps: computed(
    () => props.completed || props.forceOneLine || props.action.status === SQDCActionStatus.ACT,
  ),
});
const { defaultActionFilters } = getDefaultEABFilter();
const actionFiltersDefault = ref(defaultActionFilters);
const actionFilters = inject(ActionFilterInjectionKey, actionFiltersDefault);
const actionClicked = (event: Event) => {
  if (props.forceOneLine) {
    emit("click", event, oneLine.value);
    return;
  }

  if (oneLine.value === true) {
    oneLine.value = false;
  } else {
    emit("click", event, oneLine.value);
  }
};
const recentlyChangedAlternativeKey = ref(getRecentlyChangedAlternative());
const recentlyChanged = computed(() => {
  if (!isRecentlyChanged(props.action.updatedAt)) {
    return {};
  }
  switch (recentlyChangedAlternativeKey.value) {
    case RecentlyChangedAlternativeEnum.NORMAL:
      return { recentlyChanged: true };
    case RecentlyChangedAlternativeEnum.GREEN_WAVES:
      return { recentlyChangedAlternative: true };
    default:
      return undefined;
  }
});
const oneLineContent = computed(() => {
  if (actionRowFieldsOnEAB.value.length === 1) {
    return actionRowFieldsOnEAB.value[0].value.split("\n")[0] ?? "";
  }

  return actionRowFieldsOnEAB.value
    .map((field) => `${field.label}: ${field.value}`.split("\n")[0])
    .join(" | ");
});
const showEventModal = ref(false);
const idBadgeClicked = () => {
  emit("open-edit-component");
  if (!props.openEditExternally) {
    showEventModal.value = true;
  }
};

const propActionType = computed(() =>
  props.actionTypes.find((typeIteratee) => typeIteratee.id === props.action.typeId),
);
const { result: actionTypeResult } = useSQDCEventActionTypesQuery(
  computed(() => ({ ids: [props.action.typeId] })),
  computed(() => ({
    enabled: propActionType.value == null,
    fetchPolicy: "cache-first",
  })),
);
const actionTypeConfig = computed(
  () => propActionType.value ?? actionTypeResult.value?.SQDCEventActionTypes[0],
);
const { result: eventTypeResult } = useSQDCEventActionTypesQuery(
  computed(() => ({ ids: [props.event?.typeId ?? 0] })),
  computed(() => ({
    enabled: props.event?.typeId != null,
    fetchPolicy: "cache-first",
  })),
);
const actionEventConnection = computed(() =>
  props.event?.ActionEventConnection.find(
    (connection) => connection.action?.id === props.action.id,
  ),
);
const actionEventInfoTitle = computed(() => {
  const fieldId = actionEventConnection.value?.fieldId;
  const field =
    fieldId == null
      ? undefined
      : eventTypeResult.value?.SQDCEventActionTypes[0]?.SQDCEventActionTypeFields.find(
          (candidate) => candidate.id === fieldId,
        );

  return formatActionEventInfoText(
    field == null ? undefined : String(t(`dynamic.${field.nameId}`)),
  );
});
const actionType = computed(() => {
  const type = actionTypeConfig.value;

  if (type == null) {
    return null;
  }
  return {
    ...type,
    cards: getCardsForRevision(type.Cards, props.action.revision),
    SQDCEventActionTypeFields: type.SQDCEventActionTypeFields.filter(
      (field) =>
        field.addedByRevision <= props.action.revision &&
        (field.removedByRevision == null || field.removedByRevision > props.action.revision),
    ),
  };
});

function getTypeFieldName(
  fieldType: SQDCEventActionTypeFieldType,
  relation?: SQDCEventTypeFieldRelation,
) {
  switch (fieldType) {
    case SQDCEventActionTypeFieldType.BOOLEAN:
      return "SQDCActionBooleanFields" as const;
    case SQDCEventActionTypeFieldType.DATE:
    case SQDCEventActionTypeFieldType.DATERANGE:
      return "SQDCActionDateFields" as const;
    case SQDCEventActionTypeFieldType.ENUM:
    case SQDCEventActionTypeFieldType.REPAIR_WINDOW:
    case SQDCEventActionTypeFieldType.VETO:
    case SQDCEventActionTypeFieldType.RECOMMENDATION:
    case SQDCEventActionTypeFieldType.COMPLETION:
    case SQDCEventActionTypeFieldType.FIVE_WHY:
      return "SQDCActionTextFields" as const;
    case SQDCEventActionTypeFieldType.NUMBER:
      return "SQDCActionNumberFields" as const;
    case SQDCEventActionTypeFieldType.OUTAGE:
      return "SQDCActionOutageFields" as const;
    case SQDCEventActionTypeFieldType.PERCENT_SHARING:
      return "SQDCActionPercentSharingFields" as const;
    case SQDCEventActionTypeFieldType.RELATION:
      switch (relation) {
        case SQDCEventTypeFieldRelation.COST_CENTER:
          return "SQDCActionCostCenterFields" as const;
        case SQDCEventTypeFieldRelation.TECHNICAL_PLACE:
          return "SQDCActionTechnicalPlaceFields" as const;
        case SQDCEventTypeFieldRelation.USER:
          return "SQDCActionUserFields" as const;
        case SQDCEventTypeFieldRelation.IDEA_CATEGORY:
          return "SQDCActionIdeaCategoryFields" as const;
        case SQDCEventTypeFieldRelation.ERROR_CATALOGUE:
          return "SQDCActionErrorCatalogueFields" as const;
      }
      throw new Error(`Unsupported relation type ${relation} for field type ${fieldType}`);
    case SQDCEventActionTypeFieldType.TEXT:
    case SQDCEventActionTypeFieldType.TEXTAREA:
      return "SQDCActionTextFields" as const;
  }
  throw new Error(`Unsupported fieldType type ${fieldType}`);
}

type ButtonModuleBadge = Pick<SQDCButtonModuleOption, "icon" | "severity"> & {
  key: number;
  label: string;
};

type ActionRowFieldDisplay = {
  key: number;
  label: string;
  value: string;
  badge?: ButtonModuleBadge;
  ishikawaDiagram?: IshikawaDiagramValue;
};

type IshikawaDiagramValue = {
  name: string;
  areas: IshikawaAreaConfig[];
  reasons: {
    name: string;
    type: ReasonType;
    selected: boolean;
  }[];
};

const actionRowFieldsOnEAB = computed<ActionRowFieldDisplay[]>(() => {
  const fields =
    actionType.value?.SQDCEventActionTypeFields.filter((field) => field.showOnEAB).sort(
      sortFieldsByTemplatePosition,
    ) ?? [];

  return fields.map((field) => {
    const hasFieldAccess = hasConfiguredAccessRight(field.visibleForAdminOnly, siteId.value);
    const rows = getActionRowFieldRows(field);
    return {
      key: field.id,
      label: t(`dynamic.${field.nameId}`),
      value: hasFieldAccess
        ? formatActionRowValue(rows.map((row) => formatFieldRow(field, row)))
        : t("components.AccessRightWrapper.message"),
      badge: hasFieldAccess ? getButtonModuleBadge(field, rows) : undefined,
      ishikawaDiagram:
        hasFieldAccess && field.type === SQDCEventActionTypeFieldType.ISHIKAWA
          ? (getIshikawaDiagramValue(rows[0]?.value, field.ishikawaConfig) ?? {
              name: "",
              reasons: [],
              areas: normalizeIshikawaConfig(field.ishikawaConfig, t).areas,
            })
          : undefined,
    };
  });
});

function sortFieldsByTemplatePosition(left: ActionRowField, right: ActionRowField) {
  return (
    left.rowIndex - right.rowIndex || left.columnIndex - right.columnIndex || left.id - right.id
  );
}

const actionButtonModuleBadges = computed(() => {
  const fields =
    actionType.value?.SQDCEventActionTypeFields.filter((field) =>
      isSQDCButtonModuleType(field.type),
    ) ?? [];

  return fields
    .filter((field) => hasConfiguredAccessRight(field.visibleForAdminOnly, siteId.value))
    .map((field) => getButtonModuleBadge(field, getActionRowFieldRows(field), "nothing"))
    .filter((badge): badge is ButtonModuleBadge => badge != null);
});

type ActionRowField = NonNullable<typeof actionType.value>["SQDCEventActionTypeFields"][number];
type FieldRow = {
  fieldId: number;
  value?: unknown;
  userId?: number;
  percentage?: number;
  hasOutage?: boolean;
  startDate?: string;
  endDate?: string;
};
function getActionRowFieldRows(field: ActionRowField): FieldRow[] {
  const readonlyBinding = workflowContext.value?.step.node.variableBindings?.find(
    (binding) => binding.readonly && binding.fieldId === field.id,
  );
  if (readonlyBinding != null) {
    const value = workflowContext.value?.instance.variableValues?.[readonlyBinding.variableId];
    return value == null ? [] : [{ fieldId: field.id, value }];
  }

  if (field.type === SQDCEventActionTypeFieldType.ISHIKAWA) {
    return props.action.Ishikawa == null
      ? []
      : [{ fieldId: field.id, value: props.action.Ishikawa }];
  }

  if (field.type === SQDCEventActionTypeFieldType.ENUM_NUMBER) {
    const enumRow = props.action.SQDCActionTextFields.find((row) => row.fieldId === field.id);
    const numberRow = props.action.SQDCActionNumberFields.find((row) => row.fieldId === field.id);
    if (enumRow == null && numberRow == null) {
      return [];
    }
    return [
      {
        fieldId: field.id,
        value: {
          enumValue: enumRow?.value ?? null,
          numberValue: numberRow?.value ?? null,
        },
      },
    ];
  }

  if (field.type === SQDCEventActionTypeFieldType.REPAIR_WINDOW) {
    const enumRow = props.action.SQDCActionTextFields.find((row) => row.fieldId === field.id);
    const dateRow = props.action.SQDCActionDateFields.find((row) => row.fieldId === field.id);
    if (enumRow == null && dateRow == null) {
      return [];
    }
    return [
      {
        fieldId: field.id,
        value: {
          enumValue: enumRow?.value ?? null,
          dateValue: dateRow?.value ?? null,
        },
      },
    ];
  }

  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    const rows = props.action.SQDCActionOutageFields.filter((row) => row.fieldId === field.id);
    return rows.length > 0 ? rows : [{ fieldId: field.id, hasOutage: false }];
  }

  const fieldPropName = getTypeFieldName(field.type, field.relation);
  return (
    props.action[fieldPropName]?.filter((row: { fieldId: number }) => row.fieldId === field.id) ??
    []
  );
}

function getButtonModuleBadge(
  field: ActionRowField,
  rows: FieldRow[],
  fallbackValue?: unknown,
): ButtonModuleBadge | undefined {
  if (!isSQDCButtonModuleType(field.type)) {
    return undefined;
  }
  const value = rows.find((row) => typeof row.value === "string")?.value ?? fallbackValue;
  const option = getSQDCButtonModuleDisplayOption(field.type, value);
  return option == null
    ? undefined
    : {
        key: field.id,
        icon: option.icon,
        label: t(option.labelKey),
        severity: option.severity,
      };
}

function collectRelationIds(relation: SQDCEventTypeFieldRelation) {
  const fields =
    actionType.value?.SQDCEventActionTypeFields.filter(
      (field) =>
        field.showOnEAB &&
        hasConfiguredAccessRight(field.visibleForAdminOnly, siteId.value) &&
        field.type === SQDCEventActionTypeFieldType.RELATION &&
        field.relation === relation,
    ) ?? [];

  return [
    ...new Set(
      fields
        .flatMap((field) => getActionRowFieldRows(field))
        .map((row) => row.value)
        .filter((value): value is number => typeof value === "number"),
    ),
  ];
}

const actionRowUserIds = computed(() => collectRelationIds(SQDCEventTypeFieldRelation.USER));
const actionRowPercentSharingUserIds = computed(() => {
  const fields =
    actionType.value?.SQDCEventActionTypeFields.filter(
      (field) =>
        field.showOnEAB &&
        hasConfiguredAccessRight(field.visibleForAdminOnly, siteId.value) &&
        field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING,
    ) ?? [];

  return fields
    .flatMap((field) => getActionRowFieldRows(field))
    .map((row) => row.userId)
    .filter((userId): userId is number => typeof userId === "number");
});
const actionRowAllUserIds = computed(() => [
  ...new Set([...actionRowUserIds.value, ...actionRowPercentSharingUserIds.value]),
]);
const actionRowCostCenterIds = computed(() =>
  collectRelationIds(SQDCEventTypeFieldRelation.COST_CENTER),
);
const actionRowTechnicalPlaceIds = computed(() =>
  collectRelationIds(SQDCEventTypeFieldRelation.TECHNICAL_PLACE),
);
const actionRowErrorCatalogueIds = computed(() =>
  collectRelationIds(SQDCEventTypeFieldRelation.ERROR_CATALOGUE),
);

const { result: actionRowUsersResult } = useGetUserForRevisionListQuery(
  () => ({ ids: actionRowAllUserIds.value }),
  () => ({ enabled: actionRowAllUserIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: actionRowCostCentersResult } = useGetCostCentersForRainbowTileQuery(
  () => ({ ids: actionRowCostCenterIds.value }),
  () => ({ enabled: actionRowCostCenterIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: actionRowTechnicalPlacesResult } = useRevisionTechnicalPlacesQuery(
  () => ({ ids: actionRowTechnicalPlaceIds.value }),
  () => ({ enabled: actionRowTechnicalPlaceIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const { result: actionRowIdeaCategoriesResult } = useHLPCategoriesQuery();
const { result: actionRowErrorCataloguesResult } = useQAErrorCataloguesQuery(
  () => ({ ids: actionRowErrorCatalogueIds.value }),
  () => ({ enabled: actionRowErrorCatalogueIds.value.length > 0, fetchPolicy: "cache-first" }),
);

function formatFieldRow(field: ActionRowField, row: FieldRow): unknown {
  if (field.type === SQDCEventActionTypeFieldType.ENUM) {
    return formatEnumRowValue(field, row.value);
  }

  if (field.type === SQDCEventActionTypeFieldType.ENUM_NUMBER) {
    return formatEnumNumberValue(field, row.value);
  }

  if (field.type === SQDCEventActionTypeFieldType.REPAIR_WINDOW) {
    return formatRepairWindowValue(field, row.value);
  }

  if (isSQDCButtonModuleType(field.type)) {
    return formatSQDCButtonModuleValue(field.type, row.value, t);
  }

  if (field.type === SQDCEventActionTypeFieldType.COMPLETION) {
    return formatCompletionValue(row.value);
  }

  if (field.type === SQDCEventActionTypeFieldType.FIVE_WHY) {
    return formatFiveWhyValue(row.value);
  }

  if (field.type === SQDCEventActionTypeFieldType.ISHIKAWA) {
    return formatIshikawaValue(row.value);
  }

  if (field.type === SQDCEventActionTypeFieldType.OUTAGE) {
    if (!row.hasOutage) {
      return t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.noOutage");
    }

    const dates = [row.startDate, row.endDate]
      .filter((value): value is string => value != null)
      .map((value) => DateTime.fromISO(value))
      .filter((value) => value.isValid)
      .map((value) => value.toLocaleString(DateTime.DATETIME_SHORT));
    const outage = t("modules.sqdc.components.eventActionBoard.modals.SQDCOutageField.outage");
    return dates.length > 0 ? `${outage}: ${dates.join(" - ")}` : outage;
  }

  if (field.type === SQDCEventActionTypeFieldType.PERCENT_SHARING) {
    const userName =
      actionRowUsersResult.value?.Users.find((user) => user.id === row.userId)?.preferredName ??
      row.userId;
    return `${userName}: ${row.percentage}%`;
  }

  if (
    ![
      SQDCEventActionTypeFieldType.DATE,
      SQDCEventActionTypeFieldType.DATERANGE,
      SQDCEventActionTypeFieldType.RELATION,
    ].includes(field.type)
  ) {
    return row.value;
  }

  if (
    [SQDCEventActionTypeFieldType.DATE, SQDCEventActionTypeFieldType.DATERANGE].includes(field.type)
  ) {
    if (typeof row.value === "string") {
      return formatDate(DateTime.fromISO(row.value), field.dateGranularity, t);
    }
    return formatDate(row.value as any as DateTime, field.dateGranularity, t);
  }

  switch (field.relation) {
    case SQDCEventTypeFieldRelation.USER:
      return (
        actionRowUsersResult.value?.Users.find((user) => user.id === row.value)?.preferredName ??
        row.value
      );
    case SQDCEventTypeFieldRelation.COST_CENTER: {
      const costCenter = actionRowCostCentersResult.value?.CostCenters.find(
        (entry) => entry.id === row.value,
      );
      return costCenter == null ? row.value : `${costCenter.path} ${costCenter.name}`;
    }
    case SQDCEventTypeFieldRelation.TECHNICAL_PLACE: {
      const technicalPlace = actionRowTechnicalPlacesResult.value?.OGTechnicalPlaces.find(
        (entry) => entry.id === row.value,
      );
      return technicalPlace == null ? row.value : `${technicalPlace.path} ${technicalPlace.name}`;
    }
    case SQDCEventTypeFieldRelation.IDEA_CATEGORY: {
      const category = actionRowIdeaCategoriesResult.value?.HLPCategories.find(
        (entry) => entry.id === row.value,
      );
      return category == null ? row.value : category.name;
    }
    case SQDCEventTypeFieldRelation.ERROR_CATALOGUE: {
      const errorCatalogue = actionRowErrorCataloguesResult.value?.QAErrorCatalogues.find(
        (entry) => entry.id === row.value,
      );
      return errorCatalogue == null
        ? row.value
        : `${errorCatalogue.code} – ${t(`dynamic.${errorCatalogue.nameId}`)}`;
    }
    default:
      return row.value;
  }
}

function formatEnumRowValue(field: ActionRowField, value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  const enumValue = field.EnumValues.find((candidate) => String(candidate.languageId) === value);
  return enumValue == null ? value : t(`dynamic.${enumValue.languageId}`);
}

function formatEnumNumberValue(field: ActionRowField, value: unknown): unknown {
  const enumNumber = parseRecordValue(value);
  if (enumNumber == null) {
    return value;
  }
  const enumValue =
    typeof enumNumber.enumValue === "string" && enumNumber.enumValue.trim() !== ""
      ? formatEnumRowValue(field, enumNumber.enumValue)
      : "";
  const numberValue =
    enumNumber.numberValue == null || enumNumber.numberValue === ""
      ? ""
      : String(enumNumber.numberValue);
  return [enumValue, numberValue].filter((entry) => entry !== "").join(" / ");
}

function formatRepairWindowValue(field: ActionRowField, value: unknown): unknown {
  const repairWindow = parseRecordValue(value);
  if (repairWindow == null) {
    return value;
  }
  const enumValue =
    typeof repairWindow.enumValue === "string" && repairWindow.enumValue.trim() !== ""
      ? formatSQDCRepairWindowEnumValue(field.EnumValues, repairWindow.enumValue, locale.value, t)
      : "";
  const dateValue =
    typeof repairWindow.dateValue === "string" && repairWindow.dateValue.trim() !== ""
      ? formatDate(DateTime.fromISO(repairWindow.dateValue), field.dateGranularity, t)
      : "";
  return [enumValue, dateValue].filter((entry) => entry !== "").join(" / ");
}

function formatActionRowValue(value: unknown): string {
  if (value == null || value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? t("global.yes") : t("global.no");
  }

  if (Array.isArray(value)) {
    return value.map(formatActionRowValue).filter(Boolean).join(", ");
  }

  return String(value);
}

function parseRecordValue(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

function formatFiveWhyValue(value: unknown) {
  const fiveWhy = parseRecordValue(value);
  const items = fiveWhy == null ? (Array.isArray(value) ? value : []) : fiveWhy.items;
  if (!Array.isArray(items)) {
    return value;
  }
  return items
    .map((entry) => {
      const item = parseRecordValue(entry);
      if (item == null) {
        return String(entry);
      }
      return [item.cause, item.why1, item.why2, item.why3, item.why4, item.why5]
        .filter((part): part is string => typeof part === "string" && part.trim() !== "")
        .join(" > ");
    })
    .filter((entry) => entry !== "")
    .join("\n");
}

function formatCompletionValue(value: unknown) {
  const completion = parseRecordValue(value);
  if (completion == null) {
    return value;
  }
  const checks = [
    completion.presentedInSteeringCommittee ?? completion.steeringCommitteePresented,
    completion.reportedCompleteByTeamLead ?? completion.teamLeadCompleted,
    completion.closedByManager ?? completion.managerClosed,
  ].filter(Boolean).length;
  const hashtags = Array.isArray(completion.hashtags)
    ? completion.hashtags.filter((entry): entry is string => typeof entry === "string")
    : [];
  const checkLabel = t(
    "modules.sqdc.components.eventActionBoard.modals.SQDCRevisionModal.completionChecks",
    { count: checks, total: 3 },
  );
  return hashtags.length > 0 ? `${checkLabel}: ${hashtags.join(", ")}` : checkLabel;
}

function formatIshikawaValue(value: unknown) {
  const ishikawa = parseRecordValue(value);
  if (ishikawa == null) {
    return value;
  }
  const name = typeof ishikawa.name === "string" ? ishikawa.name : "";
  const reasons = Array.isArray(ishikawa.reasons)
    ? ishikawa.reasons.length
    : Array.isArray(ishikawa.Reason)
      ? ishikawa.Reason.length
      : 0;
  return reasons > 0
    ? `${name} (${t("modules.sqdc.components.eventActionBoard.modals.SQDCIshikawaField.causeCount", { count: reasons })})`
    : name;
}

function getIshikawaDiagramValue(
  value: unknown,
  configValue: unknown,
): IshikawaDiagramValue | undefined {
  const ishikawa = parseRecordValue(value);
  if (ishikawa == null) {
    return undefined;
  }

  const name = typeof ishikawa.name === "string" ? ishikawa.name : "";
  const reasons = Array.isArray(ishikawa.reasons)
    ? ishikawa.reasons
    : Array.isArray(ishikawa.Reason)
      ? ishikawa.Reason
      : [];

  return {
    name,
    areas: normalizeIshikawaConfig(configValue, t).areas,
    reasons: reasons.map((entry) => {
      const reason = parseRecordValue(entry);
      return {
        name: typeof reason?.name === "string" ? reason.name : "",
        type:
          typeof reason?.type === "string" && reason.type.trim() !== ""
            ? (reason.type as ReasonType)
            : "HUMAN",
        selected: reason?.selected === true,
      };
    }),
  };
}

// #region old filters
function setUserFilter(owner: EventListEventFieldsFragment["owner"], evt: MouseEvent) {
  if ((evt.ctrlKey || evt.metaKey) && owner != null) {
    actionFilters.value = {
      updatedAt: DateTime.now(),
      actionFilters: [new CreatorFilter([owner?.id])],
    };
  }
}
// #region end
function handleOneLine() {
  oneLine.value = true;
}
const userFilters = computed(() => {
  if (siteId.value == null) {
    return undefined;
  }
  return { availableOnSiteIds: [siteId.value] };
});
</script>

<style lang="scss" src="@/components/event-action/shared.scss"></style>

<style lang="scss" scoped>
.action {
  &.oneLine {
    cursor: pointer;
    padding-bottom: 0;
  }
  &.completed.oneLine {
    opacity: 0.6;
  }
  .header {
    .headerAdditions {
      text-align: right;
    }
    .oneLineContent {
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .button-module-badges {
    display: flex;
    flex: 0 1 min(24rem, 38%);
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    max-width: min(24rem, 38%);
    margin: 0 0.6rem 0 0.45rem;
    overflow: hidden;
  }

  .button-module-badge {
    display: inline-flex;
    align-items: stretch;
    min-width: 1.65rem;
    max-width: 100%;
    min-height: 1.2rem;
    overflow: hidden;
    color: var(--p-white);
    font-weight: 500;
    line-height: 1;
    vertical-align: middle;
    white-space: nowrap;
    border-radius: var(--p-border-radius-md);
  }

  .button-module-badges > .button-module-badge {
    flex: 1 1 0;
    max-width: 100%;
  }

  .button-module-badge__icon,
  .button-module-badge__label {
    display: inline-flex;
    align-items: center;
  }

  .button-module-badge__icon {
    flex: 0 0 1.65rem;
    justify-content: center;
    width: 1.65rem;
    background: rgb(0 0 0 / 18%);
  }

  .button-module-badge__label {
    flex: 1 1 auto;
    min-width: 0;
    padding: 0 0.7rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .button-module-badge--success {
    background: var(--p-green-600);
  }

  .button-module-badge--danger {
    background: var(--p-red-600);
  }

  .button-module-badge--help {
    background: var(--p-purple-600);
  }

  .button-module-badge--secondary {
    background: var(--p-slate-600);
  }

  .content {
    min-width: 0;
    min-height: 2rem;

    .action-row-fields {
      display: grid;
      grid-template-columns: max-content minmax(5em, 1fr);
      gap: 0.25rem 0.75rem;
      min-width: 0;
      font-weight: bold;

      &.single {
        display: block;
      }
    }

    .action-row-field-label {
      min-width: 0;
      color: var(--p-blue-700);
      font-weight: bold;
    }

    .action-row-field-value {
      min-width: 0;
      white-space: pre-wrap;
      word-break: break-word;

      &.single-value {
        width: 100%;
      }
    }

    .action-row-field-diagram {
      width: min(100%, 34rem);

      :deep(.ishikawa-diagram) {
        min-height: 8rem;
        max-height: 12rem;
      }
    }
  }

  .control-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
    min-width: 2.75rem;

    .collapseButton {
      padding: 3px;
      cursor: pointer;
    }
  }

  .action-title-row {
    display: flex;
    align-items: center;
    grid-column: 2;
    min-width: 0;
    min-height: 1.75rem;
    padding: 0 0rem 0.25rem;
    color: var(--p-gray-500);
    font-size: 1.166666em;
    font-weight: 700;
    line-height: 1.2;
  }

  .action-title-row__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 48rem) {
    .content .action-row-fields:not(.single) {
      grid-template-columns: minmax(0, 1fr);
    }

    .button-module-badges {
      flex-basis: 100%;
      max-width: 100%;
      margin-right: 0;
      margin-left: 0;
    }
  }
}
.pointerAll {
  pointer-events: all;
}
.pointerNone {
  pointer-events: none;
}
</style>
